import React from "react";
import PropTypes from "prop-types";
import { Card, CardBody } from "reactstrap";
import {
    metaFor, titleFor, descriptionFor, actorFor, isOwnSide, fmtRelative, fmtDate, fmtTime,
} from "./ticketTimelineMeta";

/**
 * Talebin hareket geçmişi — yatay zaman akışı.
 *
 * Akış kilometre taşlarını gösterir (talep açıldı, destek yanıtladı, ek yüklendi, öncelik/durum
 * değişti, kapandı, yeniden açıldı); yazışmanın tamamı üstteki mesaj thread'inde okunur.
 * Adımlar backend'de tek yanıtta (ticket.timeline) kronolojik geliyor, bileşen yalnız çizer.
 *
 * Kartlar tarafa göre yerleşir: bakan kişinin kendi hareketleri çizginin üstünde, karşı tarafın
 * (talep sahibi görünümünde destek, personel görünümünde müşteri) hareketleri altında durur.
 * Dar ekranda akış kendi içinde yatay kayar, sayfa düzeni bozulmaz.
 */
const styles = `
  .ticket-timeline .tl-scroll { overflow-x: auto; overflow-y: hidden; padding: .25rem 0 .35rem; }
  .ticket-timeline .tl-track {
    list-style: none; margin: 0; padding: 0;
    display: flex; align-items: stretch; min-width: min-content;
  }

  .ticket-timeline .tl-step {
    position: relative; flex: 0 0 auto; width: 11.5rem;
    display: grid; grid-template-rows: 1fr auto 1fr; justify-items: center;
  }

  /* Adımları birbirine bağlayan yatay çizgi; ilk adımda node'un solunda başlar. */
  .ticket-timeline .tl-step::before {
    content: ""; position: absolute; top: 50%; transform: translateY(-50%);
    left: 0; right: 0; height: 2px; background: #e9edf5;
  }
  .ticket-timeline .tl-step:first-child:not(.tl-tail)::before { left: 50%; }

  /* Akışın açık ucu: çizgi son adımdan sonra da devam eder, yeni gelişmeler üzerine eklenir.
     Hiç adım yokken bile çizgi görünür kalsın diye tüm genişliği kaplar. */
  .ticket-timeline .tl-tail { width: 4.5rem; }
  .ticket-timeline .tl-tail-only { width: 100%; min-width: 12rem; }

  /* Beyaz kenarlık node'u çizginin üstünde ayırır. */
  .ticket-timeline .tl-node {
    grid-row: 2; position: relative; z-index: 1;
    width: 2.35rem; height: 2.35rem; border-radius: 999px;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 1.1rem; border: 3px solid #fff;
  }

  .ticket-timeline .tl-card { width: 100%; max-width: 10.25rem; text-align: center; }
  .ticket-timeline .tl-above .tl-card { grid-row: 1; align-self: end; padding-bottom: .5rem; }
  .ticket-timeline .tl-below .tl-card { grid-row: 3; align-self: start; padding-top: .5rem; }

  .ticket-timeline .tl-title {
    font-size: .78rem; font-weight: 600; color: #1f2a44; line-height: 1.25;
    overflow-wrap: anywhere;
  }
  /* Uzun açıklama iki satırda kırpılır; tamamı title ile görülebilir. */
  .ticket-timeline .tl-desc {
    font-size: .72rem; color: #45526b; margin-top: .1rem; overflow-wrap: anywhere;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  }
  .ticket-timeline .tl-when { font-size: .68rem; color: #8a93a6; margin-top: .15rem; }

  /* Karanlık tema (body[data-layout-mode="dark"]) açıldığında akış da koyulaşır. */
  body[data-layout-mode="dark"] .ticket-timeline .tl-step::before { background: #32394e; }
  body[data-layout-mode="dark"] .ticket-timeline .tl-node { border-color: #2a3042; }
  body[data-layout-mode="dark"] .ticket-timeline .tl-title { color: #e9ecef; }
  body[data-layout-mode="dark"] .ticket-timeline .tl-desc { color: #c3cbe4; }
`;

const TicketTimeline = ({ entries, admin }) => (
    <Card className="rounded-4 border-0 shadow-sm mb-3 ticket-timeline">
        <style>{styles}</style>
        <CardBody>
            <h6 className="fw-bold text-dark mb-3">
                <i className="bx bx-history me-1" /> Hareket Geçmişi
            </h6>

            <div className="tl-scroll">
                <ol className="tl-track">
                    {entries.map((entry) => {
                        const meta = metaFor(entry.type);
                        const description = descriptionFor(entry);
                        const placement = isOwnSide(entry, admin) ? "tl-above" : "tl-below";
                        return (
                            <li className={`tl-step ${placement}`} key={`${entry.type}-${entry.id}`}>
                                <span
                                    className="tl-node"
                                    style={{ background: meta.bg, color: meta.color }}
                                    aria-hidden="true"
                                >
                                    <i className={`bx ${meta.icon}`} />
                                </span>

                                <div className="tl-card">
                                    <div className="tl-title">{titleFor(entry, admin)}</div>
                                    {description && (
                                        <div className="tl-desc" title={description}>{description}</div>
                                    )}
                                    <div className="tl-when">
                                        {actorFor(entry)} · {fmtDate(entry.occurredAt)} {fmtTime(entry.occurredAt)}
                                    </div>
                                    <div className="tl-when">{fmtRelative(entry.occurredAt)}</div>
                                </div>
                            </li>
                        );
                    })}

                    {/* Açık uç: yeni gelişmeler geldikçe akış buradan devam eder. */}
                    <li
                        className={`tl-step tl-tail${entries.length === 0 ? " tl-tail-only" : ""}`}
                        aria-hidden="true"
                    />
                </ol>
            </div>
        </CardBody>
    </Card>
);

TicketTimeline.propTypes = {
    entries: PropTypes.array,
    admin: PropTypes.bool,
};

TicketTimeline.defaultProps = {
    entries: [],
    admin: false,
};

export default TicketTimeline;
